import { useCallback, useMemo, useState } from 'react';
import { useGetList, useNotify } from 'react-admin';
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AnimationId, AnimationSummary, AntennaId } from '@wwf/sdk';
import { getAnimationInventory, updateAnimationInventory } from '@wwf/sdk';
import { AnimationLightCard } from '@app/animation/show/AnimationLightCard';
import {
    AnimationInventoryEditCellDialog,
    type AnimationInventorySaveValues,
    type EditCellState,
} from '@app/animation/inventory/AnimationInventoryEditCellDialog';

const INVENTORY_QUERY_KEY_PREFIX = ['animation_inventory_per_antenna'] as const;

type AntennaAnimationInventoryProps = {
    antennaId: AntennaId;
    antennaName: string;
};

export const AntennaAnimationInventory = ({
    antennaId,
    antennaName,
}: AntennaAnimationInventoryProps) => {
    const notify = useNotify();
    const queryClient = useQueryClient();

    const { data: animations, isPending: animationsPending } =
        useGetList<AnimationSummary>('animation', {
            pagination: { page: 0, perPage: 10000 },
            sort: { field: 'name', order: 'ASC' },
            filter: { enabled: true },
        });

    const {
        data: inventoryRows,
        isPending: inventoryPending,
        isError: inventoryError,
    } = useQuery({
        queryKey: [...INVENTORY_QUERY_KEY_PREFIX, antennaId],
        queryFn: () => getAnimationInventory(antennaId),
    });

    const inventoryByAnimationId = useMemo(() => {
        const m = new Map<
            AnimationId,
            {
                count: number;
                inventoryComment: string | null;
                noSpecificMaterialRequired: boolean;
                available?: boolean;
            }
        >();
        for (const row of inventoryRows ?? []) {
            m.set(row.animationId, {
                count: row.inventoryCount ?? 0,
                inventoryComment: row.inventoryComment,
                noSpecificMaterialRequired: row.noSpecificMaterialRequired,
                available: row.available,
            });
        }
        return m;
    }, [inventoryRows]);

    const upsertMutation = useMutation({
        mutationFn: async (payload: {
            animation_id: AnimationId;
            count: number;
            inventoryComment: string | null;
        }) => {
            await updateAnimationInventory(payload.animation_id, antennaId, {
                inventoryCount: payload.count,
                inventoryComment: payload.inventoryComment,
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: INVENTORY_QUERY_KEY_PREFIX,
            });
            notify('Inventory updated', { type: 'success' });
        },
        onError: (err: Error) => {
            notify(err.message ?? 'Update failed', { type: 'error' });
        },
    });

    const [editCell, setEditCell] = useState<EditCellState | null>(null);

    const openEdit = useCallback(
        (params: Omit<EditCellState, 'antennaId' | 'antennaName'>) => {
            setEditCell({
                antennaId,
                antennaName,
                ...params,
            });
        },
        [antennaId, antennaName]
    );

    const closeEdit = useCallback(() => {
        setEditCell(null);
    }, []);

    const handleSaveEdit = useCallback(
        (values: AnimationInventorySaveValues) => {
            if (!editCell) return;
            upsertMutation.mutate(
                {
                    animation_id: editCell.animationId,
                    count: values.count,
                    inventoryComment: values.inventoryComment,
                },
                { onSuccess: closeEdit }
            );
        },
        [closeEdit, editCell, upsertMutation]
    );

    if (animationsPending || inventoryPending) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (inventoryError) {
        return (
            <Typography color="error">
                Could not load inventory. Try again or check your permissions.
            </Typography>
        );
    }

    const animationList = animations ?? [];

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Stock count per animation for this antenna. Click count or
                comment to edit.
            </Typography>

            {!animationList.length ? (
                <Typography variant="body2" color="text.secondary">
                    No enabled animations to display.
                </Typography>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Animation</TableCell>
                                <TableCell align="center" width={100}>
                                    Count
                                </TableCell>
                                <TableCell>Comment</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {animationList.map(animation => {
                                const inventory = inventoryByAnimationId.get(
                                    animation.id
                                );
                                const count = inventory?.count ?? 0;
                                const inventoryComment =
                                    inventory?.inventoryComment ?? null;
                                const noSpecificMaterialRequired =
                                    inventory?.noSpecificMaterialRequired ??
                                    animation.noSpecificMaterialRequired ===
                                        true;
                                const commentPreview =
                                    inventoryComment &&
                                    inventoryComment.length > 80
                                        ? `${inventoryComment.slice(0, 80)}…`
                                        : inventoryComment;

                                const openEditForRow = () =>
                                    openEdit({
                                        animationId: animation.id,
                                        animationName:
                                            animation.name ?? 'Animation',
                                        count,
                                        inventoryComment,
                                        noSpecificMaterialRequired,
                                    });

                                const editableCellSx = {
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' },
                                };

                                return (
                                    <TableRow key={animation.id} hover>
                                        <TableCell>
                                            <AnimationLightCard
                                                record={{
                                                    ...animation,
                                                    available:
                                                        inventory?.available,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            onClick={openEditForRow}
                                            sx={editableCellSx}
                                        >
                                            <Typography variant="body2">
                                                {noSpecificMaterialRequired
                                                    ? 'N/A'
                                                    : count}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            onClick={openEditForRow}
                                            sx={editableCellSx}
                                        >
                                            {commentPreview ? (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {commentPreview}
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.disabled"
                                                >
                                                    —
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <AnimationInventoryEditCellDialog
                cell={editCell}
                onClose={closeEdit}
                onSave={handleSaveEdit}
                isSavePending={upsertMutation.isPending}
            />
        </Box>
    );
};
